import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { useTheme } from './ThemeProvider'
import { fetchPreference, savePreference, clearPreference } from '../api/userPreferences'
import { CHART_PALETTE_VAR_NAMES, chartPaletteToCssVars, deriveChartPalette } from './chartPalette'
import { getDashboardThemePresetHex, isDashboardThemeId } from './dashboardThemePresets'
import {
  readDashboardThemePreference,
  writeDashboardThemePreference,
  type DashboardThemeId,
} from './dashboardThemePreference'

interface DashboardThemeContextValue {
  /** The user's stored choice — a curated theme id, or `null` for "Platform Default". */
  dashboardTheme: DashboardThemeId | null
  setDashboardTheme: (next: DashboardThemeId | null) => void
}

const DashboardThemeContext = createContext<DashboardThemeContextValue | null>(null)

/**
 * Dashboard colour theme controller (IB-292) — a curated-theme USER OVERRIDE
 * layered on top of IB-291's theme-derived `--iw-chart-*` tokens (design doc
 * §2). Sibling to, NOT a modification of, `AccentProvider` — must sit
 * **inside** `ThemeProvider` (needs `effectiveTheme` to re-derive on a
 * light/dark switch, same reason `AccentProvider` does), and mounts on the
 * `/insights/dashboards*` route group only (`AppRouter.tsx`), via `<Outlet
 * />` when used as a route layout element.
 *
 * Mechanism (design doc §2, "Token layering"): the 13 `--iw-chart-*` custom
 * properties are recomputed via the SAME `deriveChartPalette`, fed the
 * chosen CURATED theme's hex — injected **inline on a dashboard-route
 * WRAPPER element**, never `<html>`. CSS custom-property inheritance lets
 * this "shadow" the `<html>`-level `AccentProvider` value for everything
 * inside the dashboard subtree, while any surface outside it keeps reading
 * the `<html>`-level value untouched. `--iw-accent`/chrome tokens are never
 * touched here (Decision 2) — only the 13 chart tokens.
 *
 * The wrapper element uses `display: contents` so it never affects layout
 * (flex/height chains in `DashboardScreen`/the View 1/3/4 panels) — CSS
 * custom properties still inherit through a `display: contents` element,
 * this is standard cascade behaviour, not a new mechanism.
 *
 * <p><b>IB-292 server-sync delta.</b> `localStorage` (read synchronously above) stays the FAST
 * first-paint source. Once authenticated, this provider additionally hydrates from the real
 * per-user preference endpoint (`GET /me/preferences/dashboardTheme`) and, if the server holds a
 * DIFFERENT value, applies it and mirrors it back into `localStorage`, so the choice follows the
 * user across devices — resolving the `TODO(IB-292/server-sync)` `dashboardThemePreference.ts`
 * itself documents. `setDashboardTheme` still writes `localStorage` synchronously AND best-effort
 * PUTs/DELETEs the server (`null` ⇒ "Platform Default" ⇒ `DELETE`, matching `dashboardThemePreference
 * .writeDashboardThemePreference`'s own null-clears-storage convention).
 */
export function DashboardThemeProvider({ children }: { children?: ReactNode }) {
  const { effectiveTheme } = useTheme()
  const [dashboardTheme, setDashboardThemeState] = useState<DashboardThemeId | null>(() =>
    readDashboardThemePreference(),
  )
  const scopeRef = useRef<HTMLDivElement>(null)
  const auth = useAuth()
  const isAuthenticated = auth?.isAuthenticated ?? false
  const hydratedRef = useRef(false)

  useEffect(() => {
    const node = scopeRef.current
    if (!node) return
    const presetHex = getDashboardThemePresetHex(dashboardTheme)
    if (presetHex === null) {
      // Platform Default (AC7) — remove any override so the <html>-level
      // AccentProvider/IB-291 value shows through untouched, byte-identical.
      for (const varName of CHART_PALETTE_VAR_NAMES) {
        node.style.removeProperty(varName)
      }
      return
    }
    const chartVars = chartPaletteToCssVars(deriveChartPalette(presetHex, effectiveTheme))
    for (const [varName, value] of Object.entries(chartVars)) {
      node.style.setProperty(varName, value)
    }
  }, [dashboardTheme, effectiveTheme])

  useEffect(() => {
    if (!isAuthenticated || hydratedRef.current) return
    hydratedRef.current = true
    const controller = new AbortController()
    void fetchPreference('dashboardTheme', controller.signal).then((serverValue) => {
      // A null/absent server value means "unset" (Platform Default) — never override an existing
      // local choice with an unset server value; only apply a server value that IS a known theme
      // id and differs from what localStorage already holds.
      if (isDashboardThemeId(serverValue) && serverValue !== readDashboardThemePreference()) {
        setDashboardThemeState(serverValue)
        writeDashboardThemePreference(serverValue)
      }
    })
    return () => controller.abort()
  }, [isAuthenticated])

  const isAuthenticatedRef = useRef(isAuthenticated)
  isAuthenticatedRef.current = isAuthenticated

  const setDashboardTheme = useCallback((next: DashboardThemeId | null) => {
    setDashboardThemeState(next)
    writeDashboardThemePreference(next)
    // Only sync to the per-user endpoint once we actually know who "you" are — an unauthenticated
    // write has no caller identity to attach to and would just 401.
    if (isAuthenticatedRef.current) {
      if (next === null) {
        void clearPreference('dashboardTheme')
      } else {
        void savePreference('dashboardTheme', next)
      }
    }
  }, [])

  const value = useMemo<DashboardThemeContextValue>(
    () => ({ dashboardTheme, setDashboardTheme }),
    [dashboardTheme, setDashboardTheme],
  )

  return (
    <DashboardThemeContext.Provider value={value}>
      <div ref={scopeRef} data-dashboard-theme-scope="" style={{ display: 'contents' }}>
        {children ?? <Outlet />}
      </div>
    </DashboardThemeContext.Provider>
  )
}

/** Throws outside a `DashboardThemeProvider` — a missing provider is a wiring bug, not a state to degrade into. */
export function useDashboardTheme(): DashboardThemeContextValue {
  const ctx = useContext(DashboardThemeContext)
  if (!ctx) {
    throw new Error('useDashboardTheme() must be called within a <DashboardThemeProvider>')
  }
  return ctx
}
